import Image from '@atoms/Image/Image';
import NavButton from '@atoms/NavButton/NavButton';
import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import { IAwardsCarousel } from '@models/IAwardsCarousel';
import HorizontalDirectionEnum from '@models/enums/HorizontalDirectionEnum';
import Carousel from '@molecules/Carousel/Carousel';
import CompactAwardsCarousel from '@molecules/CompactAwardsCarousel/CompactAwardsCarousel';
import PaginationCarousel from '@molecules/PaginationCarousel/PaginationCarousel';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import useProductPageContentStore from '@state/ProductPageStore';
import duplicateArrayItems from '@utils/helpers/duplicateArrayItems';
import slugify from '@utils/helpers/slugify';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { SwiperSlide } from 'swiper/react';

const AwardsCarousel = (props: IAwardsCarousel) => {
  const {
    anchorId = '',
    images,
    title,
    linkedImages,
    isCompact = false,
    useLightVersionCompact = false,
  } = useProductPageContentStore().productPageContent?.awardsCarousel || props;

  const slidesPerViewXl = 6;
  const isMobile = useMediaQuery(1440);
  const filteredItems = images?.filter((image) => !!image) ?? [];
  const shouldLoopXlCarousel = filteredItems.length > slidesPerViewXl;
  const xlCarouselItems = shouldLoopXlCarousel
    ? duplicateArrayItems(filteredItems, slidesPerViewXl * 2)
    : filteredItems;

  const navButtonId = `awards-carousel-${anchorId}`;

  if (!filteredItems?.length && !linkedImages?.length) {
    return null;
  }

  return (
    <section
      id={`a-${anchorId}`}
      className="component-padding bg-white text-black-100 dark:bg-black-100 dark:text-white"
      data-testid="CT-77-Awards-Carousel"
    >
      <SoftLoadWrapper>
        <div className="container relative overflow-hidden">
          <div className="flex flex-col gap-y-15 xl:grid xl:grid-cols-12 xl:gap-y-20">
            {title && (
              <div
                dangerouslySetInnerHTML={{ __html: title }}
                className={classNames('text-center', {
                  'h4 xl:col-span-8 xl:col-start-3': isCompact,
                  'h1 xl:col-span-7 xl:col-start-2 xl:text-left': !isCompact,
                })}
                data-testid="CT-77-Awards-Carousel-Title"
              />
            )}
            {!isCompact && !isMobile && shouldLoopXlCarousel && (
              <div
                className="col-span-1 col-start-11 flex items-end justify-end gap-5"
                data-testid="CT-77-Awards-Carousel-Nav-Buttons"
              >
                <NavButton
                  direction={HorizontalDirectionEnum.Left}
                  id={navButtonId}
                />
                <NavButton
                  direction={HorizontalDirectionEnum.Right}
                  id={navButtonId}
                />
              </div>
            )}

            {isCompact && !!linkedImages?.length && (
              <CompactAwardsCarousel
                linkedImages={linkedImages}
                useLightVersionCompact={useLightVersionCompact}
              />
            )}

            {isMobile && !isCompact && !!filteredItems.length && (
              <div className="overflow-swiper px-20 shadow-duplicates md:px-[200px] lg:px-[300px] xl:px-0">
                <PaginationCarousel
                  id="Awards-Carousel"
                  swiperClasses="overflow-xl"
                  paginationClasses="mt-7.5 md:mt-10"
                  loop
                  centeredSlides
                  slidesPerView={1.001} // Swiper v10 needs a value greater than 1 to properly show the prev/next slides when looping
                >
                  {filteredItems.map((image, index) => (
                    <SwiperSlide
                      key={slugify(`${image.desktop?.url}-${index}`)}
                      className="stretch-card"
                    >
                      <div
                        className="flex h-full flex-col items-center justify-center px-2 md:px-10 lg:px-8"
                        data-testid={`CT-77-Awards-Carousel-Item-${index}`}
                      >
                        <Image
                          key={image.desktop?.url}
                          classes="w-full"
                          image={image}
                          imageClasses="select-none w-full h-full object-contain min-h-[160px]"
                          sizes="300px"
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </PaginationCarousel>
              </div>
            )}

            {!isMobile && !isCompact && !!filteredItems.length && (
              <div className="xl:col-span-10 xl:col-start-2">
                <ShadowContainerHorizontal
                  showStart={shouldLoopXlCarousel}
                  showEnd={shouldLoopXlCarousel}
                  disableScroll
                >
                  <Carousel
                    className={classNames('w-full', {
                      '[&>div]:flex [&>div]:items-center [&>div]:justify-center':
                        filteredItems.length < slidesPerViewXl,
                    })}
                    keyboard={{
                      enabled: true,
                      onlyInViewport: true,
                    }}
                    slidesPerView={slidesPerViewXl}
                    spaceBetween={70}
                    loop={shouldLoopXlCarousel}
                    navId={navButtonId}
                  >
                    {xlCarouselItems.map((image, index) => (
                      <SwiperSlide
                        className="stretch-card"
                        key={slugify(`${image.desktop?.url}-${index}`)}
                      >
                        <div
                          className="relative z-40 flex h-full items-center justify-center"
                          data-testid={`CT-77-Awards-Carousel-Item-${index}`}
                        >
                          <Image
                            image={image}
                            imageClasses="w-full select-none"
                            sizes="300px"
                          />

                          <div className="absolute top-0 left-0 h-full w-[200%]" />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Carousel>
                </ShadowContainerHorizontal>
              </div>
            )}
          </div>
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default AwardsCarousel;
